import { colors, uiFont } from '../theme';
import React, { useState } from 'react';
import {
  AppBar,
  Divider,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  Stack,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Cart from './Cart';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logoFinal-mark-96.webp';

const Header = () => {
  const { isUserLogged, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileOpen(false);
  };

  const handleGoToCatalog = () => {
    setMobileOpen(false);

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById('catalogo');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 180);
      return;
    }

    const section = document.getElementById('catalogo');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navButtonStyle = {
    color: colors.text,
    display: 'block',
    borderRadius: 1,
    px: 1.1, fontFamily: uiFont, fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: colors.tint,
      color: colors.cta,
    },
  };

  const mobileLinkStyle = {
    justifyContent: 'flex-start',
    color: colors.text,
    borderRadius: 3,
    px: 1.5,
    py: 1.2,
    fontWeight: 800,
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: colors.tint,
      color: colors.cta,
    },
  };

  return (
    <Box>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: colors.page,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          color: colors.text,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, sm: 70 },
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box
              component={Link}
              to="/"
              aria-label="Ir al inicio"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                textDecoration: 'none',
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt=""
                aria-hidden="true"
                width={42}
                height={42}
                loading="eager"
                decoding="async"
                sx={{
                  height: { xs: 34, sm: 42 },
                  width: { xs: 34, sm: 42 },
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.5,
                    color: colors.text,
                    lineHeight: 1,
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  DISCOXDISCOS
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: colors.accent,
                    letterSpacing: 0.8,
                    fontWeight: 800,
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  Música sin algoritmos
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                flexGrow: 1,
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Button sx={navButtonStyle}>Inicio</Button>
              </Link>

              <Button sx={navButtonStyle} onClick={handleGoToCatalog}>
                Catalogo
              </Button>

              <Link to="/vinilos" style={{ textDecoration: 'none' }}>
                <Button sx={navButtonStyle}>Vinilos</Button>
              </Link>

              <Link to="/cds" style={{ textDecoration: 'none' }}>
                <Button sx={navButtonStyle}>CDs</Button>
              </Link>

              <Link to="/academia" style={{ textDecoration: 'none' }}>
                <Button sx={navButtonStyle}>Academia</Button>
              </Link>

              <Link to="/faqs" style={{ textDecoration: 'none' }}>
                <Button sx={navButtonStyle}>FAQs</Button>
              </Link>

              <Link to="/contact" style={{ textDecoration: 'none' }}>
                <Button sx={navButtonStyle}>Contacto</Button>
              </Link>

              {isUserLogged && (
                <Link to="/panel" style={{ textDecoration: 'none' }}>
                  <Button
                    sx={{
                      ...navButtonStyle,
                      border: '1px solid var(--dxd-border)',
                    }}
                  >
                    Panel
                  </Button>
                </Link>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                flexShrink: 0,
              }}
            >
              <Cart />

              {isUserLogged && (
                <Button
                  onClick={handleLogout}
                  sx={{
                    display: { xs: 'none', lg: 'inline-flex' },
                    color: colors.text,
                    borderRadius: 1,
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    '&:hover': {
                      backgroundColor: colors.elevated,
                    },
                  }}
                >
                  Cerrar sesion
                </Button>
              )}

              <IconButton
                aria-label="Abrir menu"
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: { xs: 'inline-flex', lg: 'none' },
                  color: colors.text,
                  border: '1px solid var(--dxd-border)',
                  ml: 0.5,
                  '&:hover': {
                    borderColor: colors.accent,
                    backgroundColor: colors.tint,
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            maxWidth: '84vw',
            backgroundColor: colors.elevated,
            borderLeft: '1px solid var(--dxd-border)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                letterSpacing: 0.8,
                color: colors.text,
              }}
            >
              MENU
            </Typography>

            <IconButton
              aria-label="Cerrar menu"
              onClick={() => setMobileOpen(false)}
              sx={{
                color: colors.text,
                border: '1px solid var(--dxd-border)',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: colors.border, mb: 2 }} />

          <Stack spacing={0.5}>
            <Link
              to="/"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileOpen(false)}
            >
              <Button fullWidth sx={mobileLinkStyle}>
                Inicio
              </Button>
            </Link>

            <Button fullWidth sx={mobileLinkStyle} onClick={handleGoToCatalog}>
              Catalogo
            </Button>

            <Link
              to="/vinilos"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileOpen(false)}
            >
              <Button fullWidth sx={mobileLinkStyle}>
                Vinilos
              </Button>
            </Link>

            <Link
              to="/cds"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileOpen(false)}
            >
              <Button fullWidth sx={mobileLinkStyle}>
                CDs
              </Button>
            </Link>

            <Link
              to="/academia"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileOpen(false)}
            >
              <Button fullWidth sx={mobileLinkStyle}>
                Academia
              </Button>
            </Link>

            <Link
              to="/faqs"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileOpen(false)}
            >
              <Button fullWidth sx={mobileLinkStyle}>
                FAQs
              </Button>
            </Link>

            <Link
              to="/contact"
              style={{ textDecoration: 'none' }}
              onClick={() => setMobileOpen(false)}
            >
              <Button fullWidth sx={mobileLinkStyle}>
                Contacto
              </Button>
            </Link>

            {isUserLogged && (
              <>
                <Link
                  to="/panel"
                  style={{ textDecoration: 'none' }}
                  onClick={() => setMobileOpen(false)}
                >
                  <Button fullWidth sx={mobileLinkStyle}>
                    Panel
                  </Button>
                </Link>

                <Button fullWidth sx={mobileLinkStyle} onClick={handleLogout}>
                  Cerrar sesion
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Drawer>

    </Box>
  );
};

export default Header;
